import type { ReactNode } from "react";

import type { TimeseriesPoint } from "@busright/data-contract";
import { COLORS } from "@busright/ui";

import { Distribution, type DistributionBin } from "./Distribution";

// Age-cohort math derived ONLY from real provisioning months (provisionedByMonth). No model split
// and no health join exist on the lifecycle payload, so cohorts are fleet-wide and honest: each
// provisioning month contributes its device count to the cohort its age falls into.

export interface AgeCohort {
  readonly label: string;
  // Inclusive lower / exclusive upper age bound in whole years.
  readonly minYears: number;
  readonly maxYears: number;
  readonly count: number;
}

// Buckets chosen around a typical ~4-year tablet service life so the "past threshold" tail is legible.
const COHORT_DEFS: readonly { label: string; minYears: number; maxYears: number }[] = [
  { label: "<1y", minYears: 0, maxYears: 1 },
  { label: "1–2y", minYears: 1, maxYears: 2 },
  { label: "2–3y", minYears: 2, maxYears: 3 },
  { label: "3–4y", minYears: 3, maxYears: 4 },
  { label: "4–5y", minYears: 4, maxYears: 5 },
  { label: "5y+", minYears: 5, maxYears: Number.POSITIVE_INFINITY },
] as const;

const MS_PER_YEAR = 365.25 * 24 * 60 * 60 * 1000;

// Age in fractional years from a "YYYY-MM" provisioning month to `asOf` (defaults to now).
const monthAgeYears = (month: string, asOf: number): number => {
  const parsed = new Date(`${month}-01T00:00:00Z`).getTime();
  if (Number.isNaN(parsed)) {
    return Number.NaN;
  }
  return (asOf - parsed) / MS_PER_YEAR;
};

export const buildAgeCohorts = (
  provisionedByMonth: readonly TimeseriesPoint[],
  asOf: number = Date.now(),
): readonly AgeCohort[] => {
  const counts = COHORT_DEFS.map(() => 0);
  for (const point of provisionedByMonth) {
    const age = monthAgeYears(point.date, asOf);
    if (Number.isNaN(age) || age < 0) {
      continue;
    }
    const idx = COHORT_DEFS.findIndex((c) => age >= c.minYears && age < c.maxYears);
    if (idx >= 0) {
      counts[idx] += point.value;
    }
  }
  return COHORT_DEFS.map((c, i) => ({ ...c, count: counts[i] }));
};

export const totalProvisioned = (provisionedByMonth: readonly TimeseriesPoint[]): number =>
  provisionedByMonth.reduce((sum, p) => sum + p.value, 0);

// Median (p50) fleet age in years, weighted by per-month device counts. Real, not modeled.
export const medianAgeYears = (
  provisionedByMonth: readonly TimeseriesPoint[],
  asOf: number = Date.now(),
): number | null => {
  const ages: { age: number; count: number }[] = [];
  let total = 0;
  for (const p of provisionedByMonth) {
    const age = monthAgeYears(p.date, asOf);
    if (Number.isNaN(age) || age < 0 || p.value <= 0) {
      continue;
    }
    ages.push({ age, count: p.value });
    total += p.value;
  }
  if (total === 0) {
    return null;
  }
  ages.sort((a, b) => a.age - b.age);
  const half = total / 2;
  let cumulative = 0;
  for (const entry of ages) {
    cumulative += entry.count;
    if (cumulative >= half) {
      return entry.age;
    }
  }
  return ages[ages.length - 1]?.age ?? null;
};

// Devices at or beyond a service-life threshold (in years). Real count from provisioning.
export const countPastThreshold = (
  provisionedByMonth: readonly TimeseriesPoint[],
  thresholdYears: number,
  asOf: number = Date.now(),
): number => {
  let count = 0;
  for (const p of provisionedByMonth) {
    const age = monthAgeYears(p.date, asOf);
    if (!Number.isNaN(age) && age >= thresholdYears) {
      count += p.value;
    }
  }
  return count;
};

export interface LcAgeCohortsProps {
  readonly cohorts: readonly AgeCohort[];
  // Cohorts at/after this index are past the typical service-life threshold and get the accent.
  readonly atRiskFromIndex: number;
  readonly height?: number;
}

// Fleet age distribution by cohort. Neutral brand bars; the past-service-life tail is accented
// (brand accent, NOT RAG — age is not a health/risk metric).
export const LcAgeCohorts = ({
  cohorts,
  atRiskFromIndex,
  height = 220,
}: LcAgeCohortsProps): ReactNode => {
  const bins: DistributionBin[] = cohorts.map((c, i) => ({
    label: c.label,
    count: c.count,
    atRisk: i >= atRiskFromIndex,
  }));
  return <Distribution bins={bins} height={height} riskColor={COLORS.accent} />;
};
